import { addDataLayerEntry } from '@printdeal/data-layer-manager';
import { LineItem } from '@printdeal/commercetools-v2/types';
import { AlgoliaHit } from 'instantsearch.js';
import {
  getFromLocalStorage, LS_KEYS, setInLocalStorage, parseStringifiedValue,
} from '../../helpers/window';
import { loadSearchInsights } from './searchInsightsService';
import { getConsentData } from '../../helpers/CookiebotHelper';

interface ItemProps {
  index: string,
  queryID: string,
  objectIDs: string[],
}

interface ClickEventSearchDataProps extends ItemProps {
  positions: number[],
  eventName: string,
  userToken: string
}

interface SearchClickEventProps extends ItemProps {
  eventName: string,
  path: string,
  objectIDs: string[],
  positions: number[],
}

const getSearchEventData = () => {
  const searchClickEvents = getFromLocalStorage(LS_KEYS.SEARCH_CLICK_EVENT);
  return searchClickEvents ? JSON.parse(searchClickEvents) as SearchClickEventProps[] : [];
};

/**
 * @deprecated Use `useConsent()` instead in the component directly
 */
export const getCookieConsentForAlgolia = (): boolean => {
  const consentData = getConsentData();
  return consentData?.statistics;
};

/**
 * ajs_user_id is set by segment in the LS. The value is either account.id or null
 */
const ajsUserId = parseStringifiedValue(getFromLocalStorage(LS_KEYS.AJS_USER_ID));

const algoliaClickEvent = async (item: AlgoliaHit, persist: boolean = true) => {
  const {
    index, queryID, objectID, position, productName, staticHeaderInView, userId,
  } = item;
  const eventAction = staticHeaderInView === false ? 'product_click_sticky' : 'product_click';
  const eventCategory = staticHeaderInView === false ? 'site_search_sticky' : 'site_search';
  const cookieConsent = getCookieConsentForAlgolia();
  addDataLayerEntry(
    eventAction,
    {
      eventCategory,
      eventLabel: productName,
      eventAction,
    },
  );

  const searchData: ClickEventSearchDataProps = {
    index,
    eventName: 'searched_item_clicked',
    queryID,
    objectIDs: [objectID],
    positions: [position + 1],
    ...(userId && cookieConsent && { userToken: ajsUserId || userId }),
  };

  // TODO(consent): use `useConsent()` hook to check if user has given consent (marketing) to store data
  // Get event Array from storage or initialize it with an empty array if first time.
  if (persist) {
    const searchEventArray = getSearchEventData();
    searchEventArray.push({
      ...searchData,
      path: item.path,
    });
    setInLocalStorage(LS_KEYS.SEARCH_CLICK_EVENT, JSON.stringify(searchEventArray));
  }

  const searchInsights = await loadSearchInsights();
  searchInsights('clickedObjectIDsAfterSearch', searchData);
};

// TO be used to get conversion data.
export const getSearchEventMatchedLineItems = (lineItems: LineItem[] = []): SearchClickEventProps[] => {
  const searchEventArray = getSearchEventData();
  const conversionDataArray: SearchClickEventProps[] = [];

  if (!searchEventArray.length) return conversionDataArray;

  return searchEventArray.filter((item) => lineItems.find((lineItem) => {
    const contentfulPageId = lineItem.custom?.fields.contentfulPageId;
    const contentfulId = item.objectIDs[0].slice(0, -5);
    return contentfulPageId === contentfulId;
  }));
};

const algoliaRemoveFromCart = (lineItems: LineItem[]) => {
  if (lineItems && lineItems.length > 0) {
    const searchEventArray = getSearchEventData();

    const newSearchEventArray = searchEventArray.filter((item) => lineItems.find((lineItem) => {
      const merchProductURL = lineItem.custom?.fields.merchProductURL;
      return merchProductURL ? merchProductURL.match(item.path) === null : true;
    }));

    // Update Event array in storage.
    setInLocalStorage(LS_KEYS.SEARCH_CLICK_EVENT, JSON.stringify(newSearchEventArray));
  }
};

/**
 * @param {Object} lineItems
 */
const algoliaConversionEvent = async (
  lineItems: LineItem[],
  indexName: string,
  userId: string | undefined = undefined,
) => {
  const items: ItemProps[] = getSearchEventMatchedLineItems(lineItems);
  algoliaRemoveFromCart(lineItems);

  if (!items.length) return;
  const cookieConsent = getCookieConsentForAlgolia();
  const { queryID } = items[0];
  const searchInsights = await loadSearchInsights();
  const insightObject = {
    index: indexName,
    eventName: 'searched_item_order',
    objectIDs: items.map((item) => item.objectIDs).flat(),
    queryID,
    ...(userId && cookieConsent && { userToken: ajsUserId || userId }),
  };

  searchInsights('convertedObjectIDs', insightObject);
};

export {
  algoliaClickEvent,
  algoliaConversionEvent,
  algoliaRemoveFromCart,
};
